/**
 * KeywordInput Component
 * Input component for entering assessment keywords
 */

import React from "react";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";

interface KeywordInputProps {
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
  maxKeywords?: number;
  tooltipText?: string;
}

export const KeywordInput: React.FC<KeywordInputProps> = ({
  value,
  onChange,
  maxLength = 50,
  maxKeywords = 5,
  tooltipText,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // Always allow deletion
    if (newValue.length < value.length) {
      onChange(newValue);
      return;
    }

    // Check character limit
    if (newValue.length > maxLength) {
      return;
    }

    // Parse keywords to check count (split by commas only)
    const parsedKeywords = newValue
      .trim()
      .split(/,+/)
      .map((word) => word.trim())
      .filter((word) => word.length > 0);

    // Don't allow input if already at max keywords
    if (parsedKeywords.length > maxKeywords) {
      return;
    }

    onChange(newValue);
  };

  // Calculate keyword count
  const keywordsList = value
    .trim()
    .split(/,+/)
    .map((word) => word.trim())
    .filter((word) => word.length > 0);

  return (
    <Box>
      {/* Label with icon above the text field */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
        <Box
          component="label"
          sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.primary" }}
        >
          Keywords
        </Box>
        {tooltipText && (
          <Tooltip title={tooltipText} arrow placement="top">
            <InfoOutlinedIcon
              sx={{
                fontSize: 16,
                color: "text.secondary",
                cursor: "help",
              }}
            />
          </Tooltip>
        )}
      </Box>

      {/* Text field */}
      <TextField
        placeholder="Enter keywords separated by commas. Max. 50 characters."
        value={value}
        onChange={handleChange}
        multiline
        rows={4}
        fullWidth
        helperText={`${value.length}/${maxLength} characters, ${keywordsList.length}/${maxKeywords} keywords`}
      />
    </Box>
  );
};
